// Shared types for PROXY's ACE workflow registry. A workflow module (see
// data/workflows/*.ts) implements ProxyWorkflowDefinition against these
// shapes; the registry (proxyWorkflowRegistry.ts) only aggregates them and
// the Intake Queue / item-detail panel only ever render against these types
// — never against a specific workflow's internals. That's what makes adding
// a workflow a new module + one registry line, not a new page/route/panel.
import type { ProxyJob } from './mockProxyJobs';
import type { ProxyInboxEmail } from './mockProxyEmails';
import type { MaterialReceiptRow } from '../types/api';
import type { TeamMessage } from '../contexts/TeamMessagesContext';

export type ProxyWorkflowType =
  | 'RDQ_ORDER' | 'SHORTAGE_FOLLOWUP' | 'MATERIAL_FOLLOWUP' | 'PROXY_CONTEST'
  | 'MATERIAL_PDF_ROUTE' | 'JOB_CHANGE_ROUTE' | 'BILLING_VERIFICATION_ROUTE' | 'CREDIT_HOLD_ROUTE'
  | 'UNMATCHED_MATERIAL_ROUTE' | 'EMAIL_BOUNCE_ROUTE' | 'MISSING_CONTACT_ROUTE' | 'FOLLOW_UP_REPLY' | 'SHORTAGE_CLOSE';

// 'email' and 'job_signal' both originate inside Proxy's own data. A third
// source, 'fulfillment_escalation', is a genuine cross-section event — a
// different section (Fulfillment) writing into Proxy's intake queue, per the
// agent-first rule that sections can feed each other without sharing a queue.
export type ProxyTaskSource = 'email' | 'job_signal' | 'fulfillment_escalation';

// Shared display copy for a task's source — used by both the Intake Queue
// table (a plain label) and the item-detail panel (chip + intro sentence),
// so a new source type is named once, not per-consumer.
export const PROXY_SOURCE_LABEL: Record<ProxyTaskSource, string> = {
  email: 'Inbound email',
  job_signal: 'Job signal',
  fulfillment_escalation: 'Fulfillment escalation',
};

export interface ProxyTaskDetailRow {
  label: string;
  value: string;
}

export interface ProxyTask {
  id: string;
  workflowType: ProxyWorkflowType;
  jobId: string;
  title: string;
  subtitle: string;
  reason: string;
  source: ProxyTaskSource;
  sourceEmailId?: string;
  // Non-email sources: a short label ("job signal", "Fulfillment escalation")
  // and a generic label/value detail card, so the panel's "why this is a
  // task" section never needs a hardcoded per-source-type component beyond
  // the one email layout (which has its own fixed from/to/subject/body shape).
  sourceLabel?: string;
  sourceDetail?: { rows: ProxyTaskDetailRow[] };
  createdAt: string;
  // Ace's confidence in this task's extraction/interpretation (0-100) — drives
  // whether the item panel opens with its source detail collapsed (>=90) or
  // expanded for review.
  confidence: number;
  // Email-sourced only — the solicitor's requested quantities, when they
  // differ from the job's on-file fullSetsNeeded/naaNeeded. Absent for
  // job-signal tasks (no explicit revised figures to seed from). RDQ_ORDER-
  // specific, but kept on the shared task shape (like the rest of ProxyTask)
  // rather than behind a per-workflow union, since one extra optional field
  // costs nothing and every other task field is already workflow-agnostic.
  revisedFullSets?: number;
  revisedNaa?: number;
  // A task covering several jobs at once (one follow-up email per solicitor,
  // ICT-1). jobId is the first of them.
  jobIds?: string[];
}

export interface ProxyWorkflowContext {
  jobs: ProxyJob[];
  emails: ProxyInboxEmail[];
  // Live Fulfillment receipts (getMaterialReceipts()), null until the first
  // fetch resolves. Only cross-section workflows (e.g. SHORTAGE_FOLLOWUP)
  // read this — RDQ_ORDER's buildTasks ignores it entirely.
  receipts: MaterialReceiptRow[] | null;
  // In-app hand-offs from Fulfillment (Received Short, No Job Found…).
  teamMessages?: TeamMessage[];
}

// ── Item-detail panel schema — what a workflow declares about ITS review
// surface. The panel component (components/proxy/ProxyTaskPanel.tsx) renders
// entirely off this data: header/stepper/why-section/footer are the fixed
// shell every workflow shares (mirrors the mock's own footer note: "same
// pattern reused for every task type; only the middle sections change");
// extractedFields/calculation/drafts/approveSummary ARE those middle
// sections, declared per workflow. ──────────────────────────────────────

export interface ProxyFieldOption {
  value: string | boolean;
  label: string;
}

export interface ProxyExtractedField {
  key: string;         // must match a key in the workflow's edit state
  label: string;
  display: string;     // read-mode text (may already include a composed hint)
  strike?: boolean;     // e.g. "Test row: ignored"
  editable?:
    | { kind: 'enum'; value: string | boolean; options: ProxyFieldOption[] }
    | { kind: 'number'; value: number };
}

export interface ProxyCalcRow {
  label: string;
  value: string;
  // 'amber' = informational escalate-to-manager flag (never a block) — e.g.
  // an outside-POD-guidelines shortage.
  tone?: 'default' | 'positive' | 'muted' | 'amber';
}

export interface ProxyCalcSchema {
  title: string;       // section 3's heading, e.g. "RDQ calculation"
  ruleText: string;     // the rule Ace applied, in its own words
  rows: ProxyCalcRow[];
  totalRow: ProxyCalcRow;
  deltaRow?: ProxyCalcRow;
}

export interface ProxyDraftSchema {
  key: string;          // matches a ProxyOutboundAction.key to splice in edits
  icon: string;
  label: string;
  to: string;
  cc?: string[];
  // Informational only (e.g. "fallback contact, no solicitor on record") —
  // shown next to the recipient, not itself editable.
  note?: string;
  subject?: string;
  body: string;
}

export interface ProxyOutboundAction {
  key: string;          // matches a ProxyDraftSchema.key when the action has a visible draft
  followUpType: string; // ProxyFollowUpRecord.type
  recipientEmail: string;
  subject: string;
  bodyPreview: string;
  // Extra recipients for the logged follow-up record (e.g. MATERIAL_FOLLOWUP's
  // Mediant CC augmentation) — ProxyFollowUpRecord already has a ccEmails
  // field; this just lets a workflow's outbound action populate it.
  ccEmails?: string[];
  // Jobs to record this on, when it isn't just the task's own job (one
  // email covering a solicitor's whole section is logged on every job in it).
  jobIds?: string[];
}

export interface ProxyReviewPanelSchema {
  extractedFields: ProxyExtractedField[];
  calculation: ProxyCalcSchema;
  drafts: ProxyDraftSchema[];
  approveSummary: string[];       // section 5 bullets, e.g. "Send both emails"
  approveConfirmation: string;    // chat message posted once approved
  outboundActions: ProxyOutboundAction[];
  // Set when an SOP says this decision is the manager's (POD outside the
  // guideline, a discounted contest fee…). Puts the task in Escalations.
  escalation?: string;
  // Tell Fulfillment what happened, in Ace (not by email), on approve.
  teamUpdate?: { kind: TeamMessage['kind']; title: string; body: string };
  // Move Fulfillment's Received Short receipt to "waiting for material"
  // (Follow Ups), so they can merge the new material when it arrives.
  awaitMaterial?: boolean;
  // This approval finishes the case: the ticket closes (Completed page).
  closesTicket?: boolean;
}

export interface ProxyPanelContext {
  task: ProxyTask;
  job: ProxyJob;
  edits: Record<string, any>;
  // The resolved source email, when task.source === 'email' — resolved once
  // by ProxyTaskPanel (against the full email list, seed + captured) and
  // handed down so a workflow's buildPanel never needs its own duplicate
  // lookup against a static import.
  email?: ProxyInboxEmail;
  // Every job a multi-job task covers (task.jobIds, resolved).
  groupJobs?: ProxyJob[];
}

export interface ProxyWorkflowDefinition {
  type: ProxyWorkflowType;
  label: string;
  // Lower-case noun phrase (with its own article) for the header subtitle
  // sentence — e.g. "an RDQ order" — kept separate from `label` (used as a
  // Title Case chip) so acronyms like "RDQ" never get mangled by a generic
  // .toLowerCase() when the shell composes "Ace ... prepared {noun} for
  // your review."
  noun: string;
  // Human-readable — surfaced nowhere in the UI yet, but declared per the
  // registry contract so a workflow's "what causes this / where does the
  // data come from" is documented in one inspectable place, not buried in
  // buildTasks' implementation.
  trigger: string;
  source: string;
  // The 3 fixed stepper labels — every workflow follows the same 3-phase
  // shape (Ace completes extraction + calc autonomously; the human only
  // validates the last step), so this is 3 strings, not an open-ended list.
  steps: [string, string, string];
  buildTasks(ctx: ProxyWorkflowContext): ProxyTask[];
  defaultEdits(task: ProxyTask, job: ProxyJob): Record<string, any>;
  buildPanel(ctx: ProxyPanelContext): ProxyReviewPanelSchema;
  // Optional — most workflows only ever need to append a followUpHistory
  // record on approve (already generic via outboundActions). PROXY_CONTEST is
  // the first workflow whose approve is a genuine state-machine transition,
  // so it needs to patch ProxyJob itself (contest.formStatus/attempts/
  // billingStatus). Returning a patch here is the one generic seam for that;
  // ProxyTaskPanel merges it via updateJob() right after logging follow-ups.
  onApprove?(ctx: ProxyPanelContext): Partial<ProxyJob> | void;
}
